<!-- eslint-disable vue/multi-word-component-names -->
<script setup>
import { ref, computed, watch, h, useAttrs } from 'vue'
import UserTag from './UserTag.vue'
import UserPicker from './UserPicker.vue'
import { useFormDisabled, useFormSize } from 'element-plus'
import { getUser } from '@/api/user'

defineOptions({
  inheritAttrs: false
})

// 用户添加图标组件 - 复用项目已有的内联SVG模式
const UserAddIcon = {
  props: { size: { type: Number, default: 16 } },
  render() {
    return h('svg', {
      fill: 'none',
      stroke: 'currentColor',
      viewBox: '0 0 24 24',
      style: { width: `${this.size}px`, height: `${this.size}px` }
    }, [
      h('path', {
        'stroke-linecap': 'round',
        'stroke-linejoin': 'round',
        'stroke-width': '2',
        d: 'M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z'
      })
    ])
  }
}

const $props = defineProps({
  modelValue: {
    type: [Number, String, Array],
    default: undefined
  },
  multiple: {
    type: Boolean,
    default: false
  },
  disabled: {
    type: Boolean,
    default: false
  },
  placeholder: {
    type: String,
    default: '请选择用户'
  },
  maxSelect: {
    type: Number,
    default: null
  },
  excludeIds: {
    type: Array,
    default: () => []
  },
  hideAddButton: {
    type: Boolean,
    default: false
  }
})
const $emits = defineEmits(['update:modelValue', 'confirm'])
const attrs = useAttrs()

// 用户信息映射表，用于编辑时根据ID查找用户信息
const userMap = ref({})

const normalizeUser = (user, id) => {
  const data = user?.data || user || {}
  return {
    id: data.id ?? id,
    name: data.displayName || data.name || data.username || String(id),
    avatar: data.avatarUrl || data.avatar
  }
}

// 加载用户信息到映射表
const loadUserInfo = async (ids) => {
  if (!ids || ids.length === 0) return
  try {
    const missingIds = ids.filter(id => id && !userMap.value[id])
    const users = await Promise.all(missingIds.map(async id => {
      try {
        return normalizeUser(await getUser(id), id)
      } catch (error) {
        console.error('加载用户信息失败:', error)
        return { id, name: String(id), avatar: '' }
      }
    }))
    users.forEach(user => {
      userMap.value[user.id] = user
    })
  } catch (error) {
    console.error('加载用户信息失败:', error)
  }
}

// 监听modelValue变化，加载用户信息
watch(() => $props.modelValue, (val) => {
  if (!val) return
  const ids = Array.isArray(val) ? val : [val]
  // 如果有纯ID需要查找用户信息
  const needLoad = ids.some(id => typeof id === 'number' && !userMap.value[id])
  if (needLoad) {
    loadUserInfo(ids)
  }
}, { immediate: true })

const valueArr = computed(() => {
  if (!$props.modelValue) return []
  const arr = Array.isArray($props.modelValue) ? $props.modelValue : [$props.modelValue]
  return arr.map(item => {
    if (typeof item === 'object') {
      return item
    }
    // 纯ID时尝试从映射表获取信息
    return userMap.value[item] || { id: item, name: '', avatar: '' }
  })
})
const userPickerRef = ref()
const formDisabled = useFormDisabled()
const formSize = useFormSize()
const disabled = computed(() => {
  return formDisabled.value || $props.disabled
})
const sizeName = computed(() => {
  const s = formSize.value || 'default'
  if (s === 'small') return 'small'
  if (s === 'large') return 'large'
  return 'default'
})
const isEmpty = computed(() => {
  if (!$props.modelValue) return true
  return Array.isArray($props.modelValue) && $props.modelValue.length === 0
})
const openUserPicker = () => {
  userPickerRef.value?.open()
}
const onPickerConfirm = (selectedUsers) => {
  selectedUsers.forEach(user => {
    if (user.id) {
      userMap.value[user.id] = { id: user.id, name: user.name || '', avatar: user.avatar || '' }
    }
  })
  $emits('confirm', selectedUsers)
}
const onClose = (id) => {
  if (!$props.modelValue) return
  if ($props.multiple && Array.isArray($props.modelValue)) {
    const newValue = $props.modelValue.filter(item => {
      const itemId = typeof item === 'object' ? item.id : item
      return String(itemId) !== String(id)
    })
    $emits('update:modelValue', newValue)
  } else {
    $emits('update:modelValue', null)
  }
}
</script>

<template>
  <user-picker ref="userPickerRef" :multiple="multiple" :model-value="modelValue" :max-select="maxSelect" :exclude-ids="excludeIds" @update:modelValue="$emit('update:modelValue', $event)" @confirm="onPickerConfirm" />
  <div
    v-bind="attrs"
    class="user-selector"
    :class="{ 'is-disabled': disabled, 'is-empty': isEmpty }"
  >
    <user-tag
      v-for="item in valueArr"
      :closable="!disabled"
      :disabled="disabled"
      :key="typeof item === 'object' ? item.id : item"
      :id="typeof item === 'object' ? item.id : item"
      :name="typeof item === 'object' ? item.name : ''"
      :avatar="typeof item === 'object' ? item.avatar : ''"
      @close="onClose(typeof item === 'object' ? item.id : item)"
    />
    <span v-show="isEmpty" class="user-selector-placeholder">{{ placeholder }}</span>
    <button
      v-if="!hideAddButton && !disabled"
      type="button"
      class="user-selector-add"
      :class="`is-${sizeName}`"
      @click="openUserPicker"
    >
      <UserAddIcon :size="sizeName === 'small' ? 12 : 14" />
      <span class="user-selector-add-text">{{ isEmpty ? '添加' : '' }}</span>
    </button>
  </div>
</template>

<style scoped lang="scss">
.user-selector {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 6px;
  width: 100%;
  min-width: 0;
  min-height: var(--form-control-height, 44px);
  max-height: 96px;
  padding: 5px 8px;
  overflow-x: hidden;
  overflow-y: auto;
  border: 1px solid var(--form-control-border, #cbd5e1);
  border-radius: var(--form-control-radius, 0.75rem);
  background-color: var(--form-control-bg, #ffffff);
  box-shadow: var(--form-control-shadow, 0 1px 2px rgba(15, 23, 42, 0.06));
  transition: border-color 0.2s ease, box-shadow 0.2s ease, background-color 0.2s ease;

  &:hover {
    border-color: var(--form-control-border-hover, #94a3b8);
  }

  &:focus-within {
    border-color: var(--form-control-focus, #2563eb);
    box-shadow: var(--form-control-focus-ring, 0 0 0 3px rgba(37, 99, 235, 0.14));
  }

  &.is-disabled {
    background-color: var(--form-control-disabled-bg, #f8fafc);
    color: var(--form-control-disabled-text, #94a3b8);
    cursor: not-allowed;
  }

  &.form-control-sm,
  &.selector-compact {
    min-height: var(--form-control-height-sm, 36px);
    max-height: 84px;
    padding: 3px 6px;
    gap: 4px;
    border-radius: var(--form-control-radius-sm, 0.625rem);
  }
}

.user-selector-placeholder {
  flex: 1 1 auto;
  min-width: 0;
  color: var(--form-control-placeholder, #94a3b8);
  font-size: 14px;
  line-height: 20px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.user-selector.form-control-sm .user-selector-placeholder,
.user-selector.selector-compact .user-selector-placeholder {
  font-size: 13px;
  line-height: 18px;
}

.user-selector.is-disabled .user-selector-placeholder {
  color: var(--form-control-disabled-text, #94a3b8);
}

.user-selector-add {
  flex: 0 0 auto;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  height: 26px;
  min-width: 26px;
  padding: 0 8px;
  margin-left: auto;
  border: 1px dashed var(--form-control-border, #cbd5e1);
  border-radius: 9999px;
  background-color: transparent;
  color: #64748b;
  font-size: 12px;
  line-height: 1;
  cursor: pointer;
  transition: border-color 0.16s ease, color 0.16s ease, background-color 0.16s ease;

  &:hover {
    border-color: var(--form-control-focus, #2563eb);
    border-style: solid;
    color: var(--form-control-focus, #2563eb);
    background-color: #eff6ff;
  }

  &.is-small {
    height: 22px;
    min-width: 22px;
    padding: 0 6px;
    font-size: 11px;
  }

  &.is-large {
    height: 30px;
    min-width: 30px;
    padding: 0 10px;
    font-size: 13px;
  }
}

.user-selector.is-disabled .user-selector-add {
  display: none;
}

.user-selector-add-text {
  font-weight: 500;
  white-space: nowrap;
}

.user-selector:not(.is-empty) .user-selector-add-text {
  display: none;
}

.user-selector:not(.is-empty) .user-selector-add {
  padding: 0;
  min-width: 26px;
}

.user-selector.form-control-sm:not(.is-empty) .user-selector-add,
.user-selector.selector-compact:not(.is-empty) .user-selector-add {
  min-width: 22px;
}

.user-selector.form-control-sm :deep(.user-tag),
.user-selector.selector-compact :deep(.user-tag) {
  --tag-h: 24px;
  --tag-pad-x: 3px;
  --tag-gap: 4px;
  --tag-avatar: 18px;
  --tag-font: 12px;
}
</style>
